import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command'
import {
  useCommandRegistry,
  useRegisterCommands
} from '@/hooks/useCommandRegistry'
import { useResourceRegistry } from '@/hooks/useResourceRegistry'
import { Search } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

/** Global ⌘K palette. Mounted once at the app root. */
export function CommandPalette() {
  const { open, setOpen, actions } = useCommandRegistry()
  const navigate = useNavigate()
  const registry = useResourceRegistry()

  // Build navigation commands from the resource registry
  const registryCommands = useMemo(() => {
    if (!registry.data) return []
    const out = []
    for (const [group, entries] of Object.entries(registry.data.groups)) {
      for (const entry of entries) {
        out.push({
          id: `nav:${entry.key}`,
          label: `Go to ${entry.label}`,
          hint: entry.description,
          group,
          keywords: [
            entry.label.toLowerCase(),
            entry.singular.toLowerCase(),
            group.toLowerCase()
          ],
          perform: () => navigate(`/r/${entry.key}`)
        })
      }
    }
    return out
  }, [registry.data, navigate])

  useRegisterCommands(registryCommands)

  // ⌘K / Ctrl+K toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  // Group actions by their `group` key
  const grouped = useMemo(() => {
    const map = new Map<string, typeof actions>()
    for (const action of actions) {
      const key = action.group ?? 'Actions'
      const list = map.get(key) ?? []
      list.push(action)
      map.set(key, list)
    }
    // Sort groups: Actions first, then alphabetical
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === 'Actions') return -1
      if (b === 'Actions') return 1
      return a.localeCompare(b)
    })
  }, [actions])

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Command Palette"
      description="Search admin actions and pages"
    >
      <CommandInput placeholder="Search actions, pages, people…" />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-1 py-4 text-sm text-muted-foreground">
            <Search className="size-4 opacity-60" />
            No matches.
          </div>
        </CommandEmpty>
        {grouped.map(([group, groupActions], idx) => (
          <div key={group}>
            {idx > 0 && <CommandSeparator />}
            <CommandGroup heading={group}>
              {groupActions.map((action) => (
                <CommandItem
                  key={action.id}
                  value={`${action.label} ${action.keywords?.join(' ') ?? ''}`}
                  onSelect={async () => {
                    setOpen(false)
                    try {
                      await action.perform()
                    } catch (err) {
                      console.error(`Command "${action.label}" failed:`, err)
                      const { toast } = await import('sonner')
                      toast.error(`"${action.label}" failed. Please try again.`)
                    }
                  }}
                >
                  {action.icon}
                  <span>{action.label}</span>
                  {action.hint && (
                    <span className="ml-auto truncate text-xs text-muted-foreground">
                      {action.hint}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
